import { useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { SegmentedControl } from './SegmentedControl'
import { chipClass } from './chip'
import { emptyValues, filterKeys } from './types'
import type { FilterSection, FilterValues, QuickFilter } from './types'

interface AdvancedFiltersDialogProps {
  sections: readonly FilterSection[]
  quickFilters?: readonly QuickFilter[]
  /** Filtros atualmente aplicados (vindos das props da página). */
  values: FilterValues
  onApply: (values: FilterValues) => void
  /** Linha explicativa sob o título — normalmente lembrando que a busca continua valendo. */
  description?: string
  /** Chaves dos campos renderizados por `children` — precisam entrar na contagem do
   *  badge, no "Limpar tudo" e na carga inicial do estado pendente. */
  extraKeys?: readonly string[]
  /** Campos extras que não cabem em SegmentedControl (ex.: faixa de datas). */
  children?: (
    values: FilterValues,
    setValue: (key: string, value: string) => void,
  ) => React.ReactNode
}

/**
 * Dialog de filtros avançados, dirigido por configuração.
 *
 * Os selects só mexem em estado local: a lista atrás não recarrega a cada escolha, só
 * quando o usuário aplica. Isso importa numa escola com milhares de alunos, onde cada
 * recarga é uma query paginada nova.
 */
export function AdvancedFiltersDialog({
  sections,
  quickFilters = [],
  values,
  onApply,
  description,
  extraKeys = [],
  children,
}: AdvancedFiltersDialogProps) {
  const keys = [...filterKeys(sections), ...extraKeys]
  const appliedCount = keys.filter((key) => values[key]).length

  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState<FilterValues>(emptyValues(keys))

  const pendingCount = keys.filter((key) => pending[key]).length

  const openDialog = () => {
    setPending(
      Object.fromEntries(
        keys.map((key) => [key, values[key] || '']),
      ) as FilterValues,
    )
    setOpen(true)
  }

  const setValue = (key: string, value: string) =>
    setPending((current) => ({
      ...current,
      [key]: value === 'all' ? '' : value,
    }))

  // Um atalho está ligado quando todos os filtros que ele representa já estão escolhidos;
  // clicar de novo desliga só esses, sem mexer no resto.
  const isQuickActive = (quick: QuickFilter) =>
    Object.entries(quick.values).every(([key, value]) => pending[key] === value)

  const toggleQuick = (quick: QuickFilter) => {
    const turningOff = isQuickActive(quick)
    setPending((current) => {
      const next = { ...current }
      Object.entries(quick.values).forEach(([key, value]) => {
        next[key] = turningOff ? '' : value
      })
      return next
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? openDialog() : setOpen(false))}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant={appliedCount > 0 ? 'secondary' : 'outline'}
        >
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Filtros avançados
          {appliedCount > 0 && (
            <Badge variant="default" className="ml-2 h-5 px-1.5">
              {appliedCount}
            </Badge>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[620px] max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle>Filtros avançados</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {quickFilters.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Atalhos</h4>
              <div className="flex flex-wrap gap-2">
                {quickFilters.map((quick) => {
                  const Icon = quick.icon
                  const selected = isQuickActive(quick)
                  return (
                    <button
                      key={quick.label}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => toggleQuick(quick)}
                      className={chipClass(selected, 'px-4 py-2')}
                    >
                      <Icon className="h-4 w-4" />
                      {quick.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {sections.map((section) => (
            <div key={section.title} className="space-y-4 border-t pt-5">
              <div>
                <h4 className="text-sm font-semibold">{section.title}</h4>
                {section.hint && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {section.hint}
                  </p>
                )}
              </div>

              {section.fields.map((field) => (
                <div key={field.key} className="flex flex-col gap-2">
                  <Label className="text-sm font-normal text-muted-foreground">
                    {field.label}
                  </Label>
                  <SegmentedControl
                    value={pending[field.key]}
                    options={field.options}
                    onChange={(value) => setValue(field.key, value)}
                  />
                </div>
              ))}
            </div>
          ))}

          {children?.(pending, setValue)}
        </div>

        <DialogFooter className="px-6 py-4 border-t sm:justify-between items-center">
          <Button
            type="button"
            variant="ghost"
            className="underline underline-offset-4 px-0 hover:bg-transparent"
            onClick={() => setPending(emptyValues(keys))}
          >
            Limpar tudo
          </Button>
          <Button
            type="button"
            onClick={() => {
              setOpen(false)
              onApply(pending)
            }}
          >
            Aplicar filtros
            {pendingCount > 0 && ` (${pendingCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
